import { Fragment, JSX, ReactNode, useCallback, useEffect, useState } from 'react';
import { Title, useAuthenticated } from 'react-admin';
import { Alert, Box, Button, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import Markdown from 'react-markdown';
import { eyebrow, MONO, numeric } from '../dashboard/components/styles/dashboard-tokens';
import {
    ChangelogEntry,
    ChangelogRelease,
    EntryGroup,
    formatReleaseDate,
    groupByType,
    loadReleases,
} from './changelogApi';

type LoadState =
    | { status: 'loading' }
    | { status: 'failed' }
    | { status: 'loaded', releases: ChangelogRelease[] };

/**
 * An entry's text is one sentence written by the team, so only inline formatting is rendered.
 * react-markdown escapes raw HTML by default; anything block-level is unwrapped to its text.
 */
const INLINE_ELEMENTS: string[] = ['p', 'strong', 'em', 'code', 'a'];

const inlineComponents = {
    p: ({ children }: { children?: ReactNode }): JSX.Element => <Fragment>{ children }</Fragment>,
    a: ({ href, children }: { href?: string, children?: ReactNode }): JSX.Element => (
        <a href={ href } target="_blank" rel="noreferrer">{ children }</a>
    ),
};

const Entry = ({ entry }: { entry: ChangelogEntry }): JSX.Element => (
    <Box component="li" sx={ { display: 'flex', alignItems: 'baseline', gap: 1.5, py: 0.75 } }>
        { /* A fixed width so the sentences start in one column down the release. */ }
        <Chip label={ entry.app } size="small" variant="outlined" sx={ { minWidth: 76, flexShrink: 0 } } />
        <Typography variant="body2" component="span" sx={ { '& code': { fontFamily: MONO, fontSize: '0.9em' } } }>
            <Markdown allowedElements={ INLINE_ELEMENTS } unwrapDisallowed components={ inlineComponents }>
                { entry.text }
            </Markdown>
            { entry.refs.length > 0 && (
                <Typography component="span" variant="caption" color="text.secondary" sx={ { ...numeric, ml: 1 } }>
                    { entry.refs.join(', ') }
                </Typography>
            ) }
        </Typography>
    </Box>
);

const Release = ({ release, latest }: { release: ChangelogRelease, latest: boolean }): JSX.Element => (
    <Box
        component="section"
        aria-label={ `Release ${ release.version }` }
        sx={ { p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper' } }
    >
        <Stack direction="row" alignItems="center" spacing={ 1.5 } useFlexGap flexWrap="wrap">
            <Typography variant="h6" component="h2" sx={ { fontWeight: 700 } }>
                { formatReleaseDate(release.date) }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={ numeric }>
                { release.version }
            </Typography>
            { latest && <Chip label="Latest" size="small" color="primary" /> }
        </Stack>

        { groupByType(release.entries).map((group: EntryGroup): JSX.Element => (
            <Box key={ group.type } sx={ { mt: 2 } }>
                <Typography component="h3" sx={ eyebrow }>{ group.type }</Typography>
                <Box component="ul" sx={ { listStyle: 'none', m: 0, mt: 0.5, p: 0 } }>
                    { group.entries.map((entry: ChangelogEntry, index: number): JSX.Element => (
                        <Entry key={ index } entry={ entry } />
                    )) }
                </Box>
            </Box>
        )) }
    </Box>
);

/**
 * What's new in HQ: every production release, newest first, read-only.
 *
 * A custom route, so react-admin does not check the session for it; `useAuthenticated` does, and
 * nothing is fetched until it has answered, for the reason given in `DashboardContainer`.
 */
export const WhatsNewPage = (): JSX.Element => {
    const { isPending, authenticated } = useAuthenticated();
    const [state, setState] = useState<LoadState>({ status: 'loading' });

    const load = useCallback(async (): Promise<void> => {
        setState({ status: 'loading' });
        try {
            setState({ status: 'loaded', releases: await loadReleases() });
        } catch {
            setState({ status: 'failed' });
        }
    }, []);

    useEffect((): void => {
        if (!isPending && authenticated) {
            void load();
        }
    }, [isPending, authenticated, load]);

    if (isPending || !authenticated) {
        return <LinearProgress sx={ { width: '100%' } } />;
    }

    return (
        <Box sx={ { pr: { xs: 0, sm: 2 }, pb: 3, maxWidth: 900 } }>
            <Title title="What's new" />
            <Box sx={ { pt: 4, pb: 2.5 } }>
                <Typography component="p" sx={ eyebrow }>{ 'Change log' }</Typography>
                <Typography variant="h5" component="h1" sx={ { fontWeight: 700, letterSpacing: '-0.01em' } }>
                    { "What's new" }
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={ { mt: 0.5 } }>
                    { 'New features, changes and fixes in each HQ release.' }
                </Typography>
            </Box>

            { state.status === 'loading' && <LinearProgress /> }

            { state.status === 'failed' && (
                <Alert
                    severity="error"
                    action={ <Button color="inherit" size="small" onClick={ (): void => { void load(); } }>Retry</Button> }
                >
                    { 'The change log could not be loaded.' }
                </Alert>
            ) }

            { state.status === 'loaded' && state.releases.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    { 'No releases have been published yet.' }
                </Typography>
            ) }

            { state.status === 'loaded' && state.releases.length > 0 && (
                <Stack spacing={ 2 }>
                    { state.releases.map((release: ChangelogRelease, index: number): JSX.Element => (
                        <Release key={ release.version } release={ release } latest={ index === 0 } />
                    )) }
                </Stack>
            ) }
        </Box>
    );
};
